import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, X, MapPin } from "lucide-react";
import { api, fmtDateUS, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (!Number.isFinite(h)) return t;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

/** Month calendar of work assignments.
 *  mode "admin": click a day to schedule a project + workers (project foreman auto-included).
 *  mode "viewer" (foreman /time): read-only — shows the days they're assigned. */
export const ScheduleCalendar = ({ mode = "admin", empFetch = null }) => {
  const isAdmin = mode === "admin";
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [entries, setEntries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selDays, setSelDays] = useState([]);
  const [jobId, setJobId] = useState("");
  const [workerSel, setWorkerSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const { data } = await api.get("/admin/schedule", { params: { month } });
        setEntries(data);
      } else if (empFetch) {
        const data = await empFetch(`/time/schedule?month=${month}`);
        setEntries(data);
      }
    } catch { /* keep last good */ }
  }, [month, isAdmin, empFetch]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/jobs").then(({ data }) => setJobs(data.filter((j) => j.status === "open"))).catch(() => {});
    api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [isAdmin]);

  const shiftMonth = (d) => {
    const [y, m] = month.split("-").map(Number);
    const nd = new Date(y, m - 1 + d, 15);
    setMonth(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
    setSelDays([]);
  };

  const [y, m] = month.split("-").map(Number);
  const firstDow = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const byDate = {};
  entries.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });
  const nowLocal = new Date();
  const todayIso = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, "0")}-${String(nowLocal.getDate()).padStart(2, "0")}`;

  const dayIso = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const toggleWorker = (id) => setWorkerSel((ws) => (ws.includes(id) ? ws.filter((w) => w !== id) : [...ws, id]));
  const toggleDay = (iso) => {
    if (isAdmin) {
      setSelDays((ds) => (ds.includes(iso) ? ds.filter((d) => d !== iso) : [...ds, iso].sort()));
    } else {
      setSelDays((ds) => (ds.includes(iso) ? [] : [iso]));
    }
  };

  const addEntry = async () => {
    if (!jobId) return toast.error("Pick a project");
    setBusy(true);
    try {
      if (isAdmin && selDays.length > 1) {
        await api.post("/admin/schedule/bulk", { dates: selDays, job_id: jobId, employee_ids: workerSel });
        toast.success(`${selDays.length} days scheduled — they show on the foreman's calendar`);
      } else {
        await api.post("/admin/schedule", { date: selDays[0], job_id: jobId, employee_ids: workerSel });
        toast.success(`Scheduled for ${fmtDateUS(selDays[0])} — it shows on the foreman's calendar`);
      }
      setJobId("");
      setWorkerSel([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/admin/schedule/${id}`);
      toast.success("Removed from the calendar");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const selEntries = selDays.flatMap((d) => byDate[d] || []);

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden" data-testid={isAdmin ? "admin-calendar" : "time-calendar"}>
      <div className="px-4 py-3.5 bg-card flex items-center justify-between">
        <button onClick={() => shiftMonth(-1)} aria-label="Previous month" data-testid="cal-prev" className="text-white/50 hover:text-primary p-1.5"><ChevronLeft size={18} /></button>
        <p className="font-display text-xl tracking-wider" data-testid="cal-month-label">{MONTHS[m - 1]} {y}</p>
        <button onClick={() => shiftMonth(1)} aria-label="Next month" data-testid="cal-next" className="text-white/50 hover:text-primary p-1.5"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 border-t border-white/10">
        {DOW.map((d) => <div key={d} className="text-center text-[9px] font-bold uppercase tracking-widest text-white/35 py-2 border-b border-white/10">{d}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={`x${i}`} className="min-h-[64px] border-b border-r border-white/5 bg-white/[0.01]" />;
          const iso = dayIso(d);
          const dayEntries = byDate[iso] || [];
          const selected = selDays.includes(iso);
          return (
            <button
              key={iso}
              onClick={() => toggleDay(iso)}
              data-testid={`cal-day-${iso}`}
              className={`min-h-[64px] border-b border-r border-white/5 p-1 text-left align-top transition-colors duration-150 ${selected ? "bg-primary/15" : "hover:bg-white/5"}`}
            >
              <span className={`text-[11px] font-bold ${iso === todayIso ? "text-primary" : "text-white/60"}`}>{d}</span>
              <div className="mt-0.5 space-y-0.5">
                {dayEntries.slice(0, 2).map((e) => (
                  <p key={e.id} className="text-[8px] leading-tight font-bold uppercase tracking-wide bg-primary/20 text-primary rounded px-1 py-0.5 truncate" data-testid={`cal-chip-${e.id}`}>{e.job_title}</p>
                ))}
                {dayEntries.length > 2 && <p className="text-[8px] text-white/40">+{dayEntries.length - 2} more</p>}
              </div>
            </button>
          );
        })}
      </div>

      {selDays.length > 0 && (
        <div className="p-4 border-t border-white/10 space-y-3 bg-card/50" data-testid="cal-day-panel">
          <p className="text-xs font-bold uppercase tracking-widest text-white/60" data-testid="cal-selected-label">
            {selDays.length === 1 ? fmtDateUS(selDays[0]) : `${selDays.length} days picked — ${fmtDateUS(selDays[0])} to ${fmtDateUS(selDays[selDays.length - 1])}`}
          </p>
          {selEntries.length === 0 && <p className="text-[11px] text-white/35">{isAdmin ? "Nothing scheduled — pick a project below." : "No assignment this day."}</p>}
          {selEntries.map((e) => (
            <div key={e.id} className="flex items-start gap-2 border border-white/10 rounded-lg bg-background/60 px-3 py-2.5" data-testid={`cal-entry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate"><span className="text-primary">{fmtDateUS(e.date)}</span> · {e.job_title} <span className="text-white/40 font-normal">· {e.client_name}</span></p>
                {e.address && <p className="text-[11px] text-white/45 flex items-center gap-1 mt-0.5"><MapPin size={10} className="shrink-0" aria-hidden="true" />{e.address}</p>}
                <p className="text-[10px] text-white/40 mt-0.5">Scheduled: {e.worker_names.join(", ")}</p>
                {e.hours_worked > 0 && (
                  <p className="text-[10px] mt-1 inline-flex items-center gap-1.5 bg-emerald-400/15 text-emerald-300 rounded-full px-2 py-0.5 font-bold" data-testid={`cal-hours-${e.id}`}>
                    {e.hours_worked}h worked{e.first_start && e.last_end ? ` · ${fmt12(e.first_start)} – ${fmt12(e.last_end)}` : ""}{e.worked_names?.length ? ` · ${e.worked_names.join(", ")}` : ""}
                  </p>
                )}
              </div>
              {isAdmin && (
                <button onClick={() => removeEntry(e.id)} aria-label="Remove assignment" data-testid={`cal-remove-${e.id}`} className="text-red-400/70 hover:text-red-300 p-1 shrink-0"><X size={14} /></button>
              )}
            </div>
          ))}
          {isAdmin && (
            <div className="space-y-2 pt-1">
              {selDays.length === 1 && selEntries.length > 0 && (
                <p className="text-[10px] font-bold uppercase tracking-widest text-primary/80" data-testid="cal-add-another-hint">
                  This day already has {selEntries.length} project{selEntries.length === 1 ? "" : "s"} — you can put another one on it below
                </p>
              )}
              <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="cal-job-select" className={selectCls}>
                <option value="" className="bg-[#252720]">Pick a project...</option>
                {jobs.map((j) => <option key={j.id} value={j.id} className="bg-[#252720]">{j.client_name} — {j.title}{j.foreman_name ? ` (${j.foreman_name})` : ""}</option>)}
              </select>
              <div className="flex flex-wrap gap-1.5">
                {employees.map((emp) => {
                  const on = workerSel.includes(emp.id);
                  return (
                    <button key={emp.id} type="button" onClick={() => toggleWorker(emp.id)} data-testid={`cal-worker-${emp.id}`} className={`px-2.5 py-1.5 rounded-full text-[10px] font-bold border transition-colors duration-150 ${on ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white/55 hover:border-primary hover:text-primary"}`}>
                      {emp.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-white/35">{isAdmin && selDays.length > 1 ? "Tap more days above to add them — one project goes on all of them. The foreman is added automatically." : "The project's foreman is added automatically. Tap extra workers to send with him."}</p>
              <button onClick={addEntry} disabled={busy} data-testid="cal-add" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs py-3 rounded-lg disabled:opacity-50">
                {busy ? "Saving..." : selDays.length > 1 ? `Schedule ${selDays.length} days at once` : `Schedule ${fmtDateUS(selDays[0])}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
